import { Button } from "@volca/ui/components/ui/button";
import { DetailField } from "@volca/ui/components/ui/detail-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { IReferral } from "@/referrals/referral";

export type ReferralConfirmAction = "lost" | "delete";

const COPY = {
  lost: {
    title: "Mark as lost?",
    description:
      "The referral leaves the board and moves to the referral history as lost.",
    confirm: "Mark as lost",
  },
  delete: {
    title: "Delete referral?",
    description:
      "The referral and its history are removed for good. This can't be undone.",
    confirm: "Delete referral",
  },
} as const;

interface ReferralConfirmationModalProps {
  referral: IReferral;
  action: ReferralConfirmAction | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const stopPropagation = (event: React.SyntheticEvent) =>
  event.stopPropagation();

export const ReferralConfirmationModal = ({
  referral,
  action,
  onOpenChange,
  onConfirm,
}: ReferralConfirmationModalProps) => {
  const copy = COPY[action ?? "lost"];

  return (
    // Its own React parent for the portal, so the backdrop's clicks stop too.
    <span
      className="contents"
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <Dialog open={!!action} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>

          <Stack align="start" gap={3}>
            <DetailField
              label="Customer"
              message={getCustomerNameAndPhone(referral.potential_customer)}
            />
            <DetailField
              label="Advocate"
              message={getCustomerNameAndPhone(referral.advocate)}
            />
            <DetailField
              label="Service"
              message={referral.service_needed || "Not set"}
            />
          </Stack>

          <DialogFooter>
            <Button
              variant="secondary"
              type="button"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              type="button"
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
            >
              {copy.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
};
